<template>
  <card class="card notes-container">
    <div class="notes-layout">
      <!-- Notes List Sidebar (Left Side) -->
      <div class="notes-sidebar">
        <div class="sidebar-header">
          <h4>Notes</h4>
          <div class="header-actions">
            <a href="#" @click.prevent="refreshNotes" class="refresh-btn">
              <em class="tim-icons icon-refresh-02" :class="refreshingAnimation"></em>
            </a>
            <button type="button" class="btn btn-primary btn-sm" @click="createNewNote">
              <i class="tim-icons icon-simple-add"></i>
            </button>
          </div>
        </div>

        <div class="notes-list">
          <div v-if="isLoading" class="empty-state">
            <p>Loading...</p>
          </div>
          <div v-else-if="notes.length === 0" class="empty-state">
            <i class="tim-icons icon-notes"></i>
            <p>No notes yet</p>
            <button type="button" class="btn btn-primary" @click="createNewNote">
              Create First Note
            </button>
          </div>
          <div v-else>
            <div
              v-for="note in notes"
              :key="note.id"
              class="note-item"
              :class="{ active: selectedNote && selectedNote.id === note.id }"
              @click="selectNote(note)"
            >
              <div class="note-item-header">
                <h5 class="note-title">{{ note.title }}</h5>
                <small class="note-date">{{ formatDate(note.created_at) }}</small>
              </div>
              <div class="note-preview" v-html="truncateHtml(note.content, 60)"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Editor Area (Right Side) -->
      <div class="notes-editor">
        <div v-if="!selectedNote && notes.length === 0" class="empty-editor">
          <i class="tim-icons icon-notes"></i>
          <h3>Welcome to Notes</h3>
          <p>Create your first note to get started</p>
          <!-- <button type="button" class="btn btn-primary" @click="createNewNote">
            <i class="tim-icons icon-simple-add"></i> Create Note
          </button> -->
        </div>

        <div v-else-if="selectedNote" class="editor-content">
          <div class="editor-header">
            <base-input
              v-model="noteForm.title"
              placeholder="Note Title"
              class="note-title-input"
            />
            <div class="editor-actions">
              <base-button
                type="danger"
                size="sm"
                icon
                @click="deleteNote(selectedNote.id)"
                title="Delete Note"
              >
                <i class="tim-icons icon-trash-simple"></i>
              </base-button>
              <base-button
                type="success"
                size="sm"
                @click="saveNote"
                :disabled="!noteForm.title.trim()"
              >
                <i class="tim-icons icon-check-2"></i> Save
              </base-button>
            </div>
          </div>

          <div class="editor-body">
            <vue-editor
              v-model="noteForm.content"
              :editorToolbar="customToolbar"
              placeholder="Write your note here..."
            ></vue-editor>
          </div>

          <div class="editor-footer">
            <small class="text-muted">
              Last updated: {{ formatDate(selectedNote.updated_at || selectedNote.created_at) }}
            </small>
            <small v-if="autosaveStatus === 'saving'" class="autosave-status saving">
              <i class="tim-icons icon-refresh-02 rotating"></i> Saving...
            </small>
            <small v-else-if="autosaveStatus === 'saved'" class="autosave-status saved">
              <i class="tim-icons icon-check-2"></i> Saved
            </small>
            <small v-else-if="autosaveStatus === 'error'" class="autosave-status error">
              <i class="tim-icons icon-simple-remove"></i> Autosave failed
            </small>
          </div>
        </div>
      </div>
    </div>
  </card>
</template>

<script>
import axios from "axios";
import { VueEditor } from "vue2-editor";

export default {
  name: "Notes",
  components: {
    VueEditor
  },
  data() {
    return {
      isLoading: true,
      isRefreshing: false,
      refreshingAnimation: "",
      notes: [],
      selectedNote: null,
      noteForm: {
        title: "",
        content: ""
      },
      autosaveStatus: "", // "", "saving", "saved", "error"
      customToolbar: [
        [{ 'header': [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['link', 'code-block'],
        ['clean']
      ]
    };
  },
  watch: {
    isRefreshing() {
      this.refreshingAnimation = this.isRefreshing ? "rotating" : "";
    },
    noteForm: {
      deep: true,
      handler() {
        if (!this.selectedNote) return;
        clearTimeout(this._autosaveTimer);
        this._autosaveTimer = setTimeout(() => {
          this.autosave();
        }, 1500);
      }
    }
  },
  mounted() {
    this.getNotes();
  },
  methods: {
    getNotes() {
      this.isLoading = true;
      axios.get('/notes')
        .then(response => {
          this.notes = Array.isArray(response.data.data) ? response.data.data : response.data;
          // Auto-select first note if available and no note is currently selected
          if (this.notes.length > 0 && !this.selectedNote) {
            this.selectNote(this.notes[0]);
          }
        })
        .catch(error => {
          console.error("Error fetching notes:", error);
          this.$toast.error("Failed to fetch notes");
        })
        .finally(() => {
          this.isLoading = false;
          this.isRefreshing = false;
        });
    },
    selectNote(note) {
      clearTimeout(this._autosaveTimer);
      this.autosaveStatus = "";
      this.selectedNote = note;
      this.noteForm.title = note.title;
      this.noteForm.content = note.content;
    },
    createNewNote() {
      clearTimeout(this._autosaveTimer);
      this.autosaveStatus = "";
      this.selectedNote = {
        id: null,
        title: "",
        content: "",
        created_at: new Date().toISOString()
      };
      this.noteForm = {
        title: "",
        content: ""
      };
    },
    saveNote() {
      if (!this.noteForm.title.trim()) {
        this.$toast.warning("Please enter a note title");
        return;
      }

      if (this.selectedNote.id) {
        // Update existing note
        axios.put(`/notes/${this.selectedNote.id}`, this.noteForm)
          .then(() => {
            this.$toast.success("Note updated successfully");
            this.getNotes();
          })
          .catch(error => {
            console.error("Error updating note:", error);
            this.$toast.error("Failed to update note");
          });
      } else {
        // Create new note
        axios.post('/notes', this.noteForm)
          .then(() => {
            this.$toast.success("Note created successfully");
            this.selectedNote = null;
            this.getNotes();
          })
          .catch(error => {
            console.error("Error creating note:", error);
            this.$toast.error("Failed to create note");
          });
      }
    },
    deleteNote(id) {
      if (confirm('Are you sure you want to delete this note?')) {
        axios.delete(`/notes/${id}`)
          .then(() => {
            this.$toast.success("Note deleted successfully");
            this.selectedNote = null;
            this.getNotes();
          })
          .catch(error => {
            console.error("Error deleting note:", error);
            this.$toast.error("Failed to delete note");
          });
      }
    },
    autosave() {
      if (!this.noteForm.title.trim()) return;
      this.autosaveStatus = "saving";
      const request = this.selectedNote.id
        ? axios.put(`/notes/${this.selectedNote.id}`, this.noteForm)
        : axios.post('/notes', this.noteForm);

      request
        .then(response => {
          // If it was a new note, assign the returned id so subsequent autosaves update it
          if (!this.selectedNote.id && response.data) {
            const created = response.data.data || response.data;
            if (created.id) {
              this.selectedNote.id = created.id;
            }
          }
          this.selectedNote.updated_at = new Date().toISOString();
          this.autosaveStatus = "saved";
          // Silently refresh the sidebar list
          axios.get('/notes').then(res => {
            this.notes = Array.isArray(res.data.data) ? res.data.data : res.data;
          });
        })
        .catch(() => {
          this.autosaveStatus = "error";
        });
    },
    refreshNotes() {
      this.isRefreshing = true;
      this.getNotes();
    },
    truncateHtml(html, maxLength) {
      const div = document.createElement('div');
      div.innerHTML = html;
      const text = div.textContent || div.innerText || '';
      if (text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength) + '...';
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const now = new Date();
      const diff = now - d;
      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (days > 7) {
        return d.toLocaleDateString();
      } else if (days > 0) {
        return `${days}d ago`;
      } else if (hours > 0) {
        return `${hours}h ago`;
      } else if (minutes > 0) {
        return `${minutes}m ago`;
      } else {
        return 'Just now';
      }
    }
  }
};
</script>

<style scoped>
.notes-layout {
  display: flex;
  height: 560px;
  gap: 0;
  overflow: hidden;
}

/* Notes Sidebar (Left Side) */
.notes-sidebar {
  width: 320px;
  border-right: 1px solid #2b3553;
  display: flex;
  flex-direction: column;
  background-color: #1e1e2f;
  order: 1;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid #2b3553;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #1a1a2e;
}

.sidebar-header h4 {
  margin: 0;
  font-size: 1.2rem;
  color: #fff;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.refresh-btn {
  color: #fff;
  font-size: 18px;
  transition: all 0.3s;
}

.refresh-btn:hover {
  color: #1d8cf8;
}

.notes-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.note-item {
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #27293d;
  border: 1px solid transparent;
}

.note-item:hover {
  background-color: #2b3553;
  border-color: #1d8cf8;
}

.note-item.active {
  background-color: #1d8cf8;
  border-color: #1d8cf8;
}

.note-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.note-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #fff;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-date {
  font-size: 0.75rem;
  color: #888;
  white-space: nowrap;
  margin-left: 10px;
}

.note-preview {
  font-size: 0.85rem;
  color: #aaa;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-item.active .note-title,
.note-item.active .note-preview {
  color: #fff;
}

.note-item.active .note-date {
  color: rgba(255, 255, 255, 0.8);
}

/* Main Editor Area (Right Side) */
.notes-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #27293d;
  order: 2;
  overflow-y: auto;
}

.empty-state,
.empty-editor {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #888;
  text-align: center;
  padding: 40px;
}

.empty-state i,
.empty-editor i {
  font-size: 64px;
  margin-bottom: 20px;
  color: #555;
}

.empty-editor h3 {
  color: #fff;
  margin-bottom: 10px;
}

.empty-editor p {
  color: #888;
  margin-bottom: 20px;
}

.editor-content {
  display: flex;
  flex-direction: column;
}

.editor-header {
  padding: 20px;
  border-bottom: 1px solid #2b3553;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  background-color: #1e1e2f;
}

.note-title-input {
  flex: 1;
  margin: 0;
}

.note-title-input >>> input {
  font-size: 1.5rem;
  font-weight: 600;
  background-color: #2b3553;
  border: 1px solid #2b3553;
  color: #fff;
  padding: 12px 16px;
}

.note-title-input >>> input:focus {
  background-color: #2b3553;
  border-color: #1d8cf8;
}

.editor-actions {
  display: flex;
  gap: 10px;
}

.editor-body {
  flex: 1;
  padding: 20px;
}

.editor-footer {
  padding: 15px 20px;
  border-top: 1px solid #2b3553;
  background-color: #1e1e2f;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.autosave-status {
  display: flex;
  align-items: center;
  gap: 4px;
}

.autosave-status.saving { color: #888; }
.autosave-status.saved  { color: #00bf9a; }
.autosave-status.error  { color: #fd5d93; }

/* Dark theme for Quill Editor */
::v-deep .quillWrapper {
  background-color: transparent;
}

::v-deep .ql-toolbar {
  background-color: #1e1e2f;
  border: 1px solid #2b3553;
  border-radius: 8px 8px 0 0;
}

::v-deep .ql-container {
  background-color: #2b3553;
  border: 1px solid #2b3553;
  border-radius: 0 0 8px 8px;
  font-size: 1rem;
  height: auto;
}

::v-deep .ql-editor {
  min-height: 200px;
  color: #fff;
}

::v-deep .ql-editor.ql-blank::before {
  color: #888;
}

::v-deep .ql-toolbar .ql-stroke {
  stroke: #fff;
}

::v-deep .ql-toolbar .ql-fill {
  fill: #fff;
}

::v-deep .ql-toolbar .ql-picker-label {
  color: #fff;
}

::v-deep .ql-toolbar button:hover,
::v-deep .ql-toolbar button:focus,
::v-deep .ql-toolbar button.ql-active {
  color: #1d8cf8;
}

::v-deep .ql-toolbar button:hover .ql-stroke,
::v-deep .ql-toolbar button:focus .ql-stroke,
::v-deep .ql-toolbar button.ql-active .ql-stroke {
  stroke: #1d8cf8;
}

::v-deep .ql-toolbar button:hover .ql-fill,
::v-deep .ql-toolbar button:focus .ql-fill,
::v-deep .ql-toolbar button.ql-active .ql-fill {
  fill: #1d8cf8;
}

::v-deep .ql-picker-options {
  background-color: #2b3553;
  border-color: #1d8cf8;
}

::v-deep .ql-picker-item {
  color: #fff;
}

::v-deep .ql-picker-item:hover {
  color: #1d8cf8;
}

.rotating {
  animation: rotating 1s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling for dark theme */
.notes-list::-webkit-scrollbar,
.editor-body::-webkit-scrollbar {
  width: 8px;
}

.notes-list::-webkit-scrollbar-track,
.editor-body::-webkit-scrollbar-track {
  background: #1e1e2f;
}

.notes-list::-webkit-scrollbar-thumb,
.editor-body::-webkit-scrollbar-thumb {
  background: #2b3553;
  border-radius: 4px;
}

.notes-list::-webkit-scrollbar-thumb:hover,
.editor-body::-webkit-scrollbar-thumb:hover {
  background: #1d8cf8;
}

/* Responsive design */
@media (max-width: 992px) {
  .notes-layout {
    flex-direction: column;
  }

  .notes-sidebar {
    width: 100%;
    height: 300px;
    border-left: none;
    border-bottom: 1px solid #2b3553;
    order: 1;
  }

  .notes-editor {
    order: 2;
  }
}
</style>
